/**
 * @file UserFeedback.jsx
 * @description Form for user to send NCO bug reports or feature suggestions
 *
 * @version 1.0.0
 * @author Carmelino Galang
 * @copyright Copyright (c) 2024, NetCyberOps LLC
 */

import React, {useState, useContext, useRef} from "react";
import {
    Form,
    Modal,
    InputGroup,
    Row,
    Image
} from "react-bootstrap/";
import {X, ImagePlus, Trash2, MessageSquareText} from "lucide-react";
import {KeycloakContext} from "@context/KeycloakContext.jsx";
import * as Sentry from "@sentry/react";
import Button from "@components/Button/Button";
import IconButton from "@components/Button/IconButton";
import "./UserFeedback.css";

export default function UserFeedback({
                                         modalShow,
                                         setModalShow,
                                         setShowToast,
                                         setToastMessage,
                                     }) {
    const {user} = useContext(KeycloakContext);

    const [message, setMessage] = useState("");
    const [sendingEmail, setSendingEmail] = useState(false);
    const [error, setError] = useState({});
    const [screenshot, setScreenshot] = useState(null);
    const [screenshotPreview, setScreenshotPreview] = useState(null);
    const fileInputRef = useRef(null);

    const handleClose = () => {
        setModalShow(false);
        setMessage("");
        setError("");
        setScreenshot(null);
        setScreenshotPreview(null);
    };

    const handleScreenshotChange = (e) => {
        if (e.target.files && e.target.files[0]) {
            const file = e.target.files[0];
            setScreenshot(file);

            // Create preview URL
            const reader = new FileReader();
            reader.onloadend = () => {
                setScreenshotPreview(reader.result);
            };
            reader.readAsDataURL(file);
        }
    };

    const removeScreenshot = () => {
        setScreenshot(null);
        setScreenshotPreview(null);
        if (fileInputRef.current) {
            fileInputRef.current.value = "";
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        setError({});
        const errors = {};

        // Validations
        const trimmedMessage = message.trim();
        if (!trimmedMessage) errors.message = "Message is required";

        if (Object.keys(errors).length > 0) return setError(errors);

        setSendingEmail(true);
        try {
            const feedbackData = {
                message: trimmedMessage || 'No message provided',
                name: user.display_name || user.firstName + ' ' + user.lastName,
                email: user.email || null
            };

            // Clear any previous attachments from the scope
            Sentry.getCurrentScope().clearAttachments();

            if (screenshot) {
                // Get binary data directly from the File object
                const buffer = await screenshot.arrayBuffer();
                const data = new Uint8Array(buffer);

                // Add the attachment to the current scope
                Sentry.getCurrentScope().addAttachment({
                    filename: screenshot.name,
                    data,
                });
            }

            Sentry.captureFeedback(feedbackData);

            handleClose();
            setShowToast(true);
            setToastMessage(`✅ NetCyberOps has been notified. Thank you for your feedback! We will respond as soon as possible.`);
        } catch (error) {
            setShowToast(true);
            setToastMessage(`❌ Error sending message to NetCyberOps: ${error}`);
        } finally {
            setSendingEmail(false);
        }
    };

    return (
        <Modal
            show={modalShow}
            onHide={handleClose}
            aria-labelledby="contained-modal-title-vcenter"
            centered
            className="ad-modal feedback-modal"
        >
            <Modal.Header className="ad-modal-header">
                <div className="ad-head-icon">
                    <MessageSquareText />
                </div>
                <div className="ad-head-text">
                    <h2 className="ad-head-title">Send us your feedback!</h2>
                    <p className="ad-head-sub">Got suggestions? We'd love to hear them!</p>
                </div>
                <IconButton icon={<X size={18}/>} label="Close" onClick={handleClose}/>
            </Modal.Header>
            <hr className="nco-divider"/>
            <Modal.Body className="ad-modal-body feedback-modal-body">
                <Form
                    onSubmit={handleSubmit}
                    className="d-flex flex-column"
                    id="feedback-form"
                >
                    <Row className="user-feedback-modal-row">
                        <Form.Group className="feedback-field">
                            <InputGroup hasValidation>
                                <Form.Control
                                    as="textarea"
                                    rows={10}
                                    id="user-feedback-message-input"
                                    placeholder="Enter your feedback..."
                                    value={message}
                                    onChange={(event) => {
                                        setMessage(event.target.value);
                                        if (error.message && event.target.value.trim())
                                            setError({...error, message: ""});
                                    }}
                                    maxLength={3000}
                                    isInvalid={!!error.message} // !! to convert to bool
                                />
                                <Form.Control.Feedback type="invalid">
                                    {error.message}
                                </Form.Control.Feedback>
                            </InputGroup>
                        </Form.Group>
                    </Row>
                    <Form.Group className="feedback-screenshot-row">
                        <div className="d-flex align-items-center gap-2">
                            <input
                                type="file"
                                accept="image/*"
                                onChange={handleScreenshotChange}
                                className="d-none"
                                ref={fileInputRef}
                            />
                            <Button
                                className="feedback-screenshot-btn"
                                variant="secondary"
                                onClick={() => fileInputRef.current.click()}
                                icon={<ImagePlus size={16}/>}
                            >
                                {screenshot ? 'Change screenshot' : 'Add screenshot'}
                            </Button>
                            {screenshot ? (
                                <Button
                                    className="feedback-screenshot-btn"
                                    variant="danger"
                                    onClick={removeScreenshot}
                                    icon={<Trash2 size={16}/>}
                                >
                                    Remove
                                </Button>
                            ) : (<> </>)}
                        </div>
                        <div id="user-feedback-char-count">
                            {message.length} / 3000 characters
                        </div>
                    </Form.Group>
                    {screenshotPreview && (
                        <Row className="feedback-field screenshot-preview">
                            <Image
                                src={screenshotPreview}
                                alt="Screenshot Preview"
                                thumbnail
                            />
                        </Row>
                    )}
                </Form>
            </Modal.Body>
            <Modal.Footer className="ad-modal-footer">
                <Button variant="secondary" onClick={handleClose}>Cancel</Button>
                <Button variant="primary"
                        type="submit"
                        form="feedback-form"
                        onClick={handleSubmit}
                        loading={sendingEmail}>
                    Submit
                </Button>
            </Modal.Footer>
        </Modal>
    );
}